import { PATH } from '@/constants/path';
import { NAVIGATION_MAP } from '@/modules/LandingHeader/config';
import { CmsNavItem } from '@/types/cms';
import { NavItem, NavItemId } from '@/types/navigation';
import { getNavigationItems } from '@/utils/browserCMSAccess';
import { tryToNoticeClientError } from '@/utils/clientErrorInfo';
import { createContext, useEffect, useState } from 'react';

export const NavigationContext = createContext<NavItem[]>([]);

// Pages built in code rather than the CMS can't be linked from the CMS navigation menu, so they're
// appended to their section's sub-items here (for both the header dropdown and the footer), unless
// the CMS already lists them. They only appear while the CMS menu includes their section: if a
// section is removed in Strapi, its whole menu goes, static links included.
const STATIC_SUB_ITEMS: Partial<Record<NavItemId, NavItem[]>> = {
  about_us_pages: [{ label: 'Celebrating 20 years', to: PATH.CELEBRATING_20_YEARS }],
};

const withStaticSubItems = (id: NavItemId, cmsSubItems: NavItem[]): NavItem[] => {
  const staticSubItems = (STATIC_SUB_ITEMS[id] ?? []).filter(
    staticItem => !cmsSubItems.some(cmsItem => cmsItem.to === staticItem.to),
  );
  return [...cmsSubItems, ...staticSubItems];
};

export function mapCmsNavData(data?: CmsNavItem[]): NavItem[] {
  if (!data) {
    return [];
  }
  return data.map<NavItem>(item => {
    const configItem = NAVIGATION_MAP[item.id];
    return {
      id: item.id,
      label: item.displayName ? item.displayName : configItem.label,
      to: configItem.to,
      trackEventName: configItem.trackEventName,
      testid: configItem.to,
      subItems: withStaticSubItems(
        item.id,
        item.childItems
          ? item.childItems.map(subItem => ({
              label: subItem.title,
              to: `${configItem.to}/${subItem.slug}`,
            }))
          : [],
      ),
    };
  });
}

export const NavigationContextProvider = ({ children }) => {
  // Try to get the serverside cached version first, so we can serverside render it
  const initialState = global.__navigationMenuData
    ? mapCmsNavData(global.__navigationMenuData)
    : [];
  const [navItems, setNavItems] = useState<NavItem[]>(initialState);

  useEffect(() => {
    (async () => {
      if (navItems && navItems.length > 0) {
        return;
      }
      // Then try to get the serverside cached version but clientside render it
      if (window.__navigationMenuData) {
        setNavItems(mapCmsNavData(window.__navigationMenuData));
        return;
      }
      // If we couldn't get the serverside copy of the navigation data, then still try to get it from
      // the CMS clientside.
      const fetchedData: CmsNavItem[] = await getNavigationItems();

      if (fetchedData?.length > 0) {
        setNavItems(mapCmsNavData(fetchedData));
        return;
      }

      // If we get to this point then we have not been able to load the navigation menu.
      // We should alert in Sentry so we can track how often this happens.
      const error = new Error('Could not load navigation menu from serverside data or CMS');
      tryToNoticeClientError(error);
    })();
  }, []);

  return <NavigationContext.Provider value={navItems}>{children}</NavigationContext.Provider>;
};
